import { Icon } from '@iconify/react';
import { Badge } from '@/components/shadcn/badge';
import { Button } from '@/components/shadcn/button';
import { Card } from '@/components/shadcn/card';
import type { PadelGameListItem } from '@/services/padelService';
import { countSetWins } from './padelScoringUtils';
import { formatPadelSessionTypeLabel, playersLabel } from './padelUiUtils';

interface PadelMatchCardProps {
  game: PadelGameListItem;
  sessionType: string;
  rotationFormat?: string;
  index: number;
  gameNumber?: number;
  canEdit: boolean;
  isSparring?: boolean;
  teamAName?: string;
  teamBName?: string;
  onEditScore?: () => void;
  onEditLineup?: () => void;
  onDelete?: () => void;
}

function scoreLine(side: 'a' | 'b', game: PadelGameListItem): string {
  const sets = (game.setScores ?? []).filter(
    (set) => set.isComplete || set.scoreSideA > 0 || set.scoreSideB > 0
  );
  if (sets.length > 0) {
    return sets.map((set) => (side === 'a' ? set.scoreSideA : set.scoreSideB)).join(' · ');
  }
  return String(side === 'a' ? (game.scoreSideA ?? 0) : (game.scoreSideB ?? 0));
}

export default function PadelMatchCard({
  game,
  sessionType,
  rotationFormat,
  index,
  gameNumber,
  canEdit,
  isSparring,
  teamAName = 'Tim A',
  teamBName = 'Tim B',
  onEditScore,
  onEditLineup,
  onDelete,
}: PadelMatchCardProps) {
  const number = gameNumber ?? index + 1;
  const scheduled = game.status === 'scheduled';
  const setRows = game.setScores ?? [];
  const hasSets = setRows.some((set) => set.scoreSideA > 0 || set.scoreSideB > 0);
  const { winsA, winsB } = countSetWins(setRows);
  const scoreA = game.scoreSideA ?? 0;
  const scoreB = game.scoreSideB ?? 0;
  const hasScore = hasSets || scoreA > 0 || scoreB > 0;
  const sideAWonByScore = hasSets ? winsA > winsB : scoreA > scoreB;
  const sideBWonByScore = hasSets ? winsB > winsA : scoreB > scoreA;
  const winnerA =
    !scheduled && (game.winnerSide === 'a' || game.winnerSide === 'side_a' || sideAWonByScore);
  const winnerB =
    !scheduled && (game.winnerSide === 'b' || game.winnerSide === 'side_b' || sideBWonByScore);
  const fallbackA = isSparring ? teamAName : 'Tim A';
  const fallbackB = isSparring ? teamBName : 'Tim B';
  const labelA = playersLabel(game.sideAPlayers) || fallbackA;
  const labelB = playersLabel(game.sideBPlayers) || fallbackB;

  const sideRow = (label: string, won: boolean, line: string) => (
    <div className="flex items-start justify-between gap-3">
      <p className={`min-w-0 flex-1 text-sm ${won ? 'font-bold text-[#0E1726]' : 'text-[#0E1726]'}`}>
        {won && <Icon icon="mdi:check-circle" className="mr-1 inline h-4 w-4 text-emerald-500" />}
        {label}
      </p>
      {!scheduled && (
        <span className={`text-sm ${won ? 'font-bold text-[#0E1726]' : 'text-[#0E1726]'}`}>{line}</span>
      )}
    </div>
  );

  return (
    <Card className="rounded-2xl border-0 bg-white p-4 shadow-[inset_0_0_0_1px_#E0E6ED]">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Badge className="border-0 bg-[#FFF1EC] px-2.5 py-1 text-[10px] font-bold text-[#FC480D] hover:bg-[#FFF1EC]">
            {formatPadelSessionTypeLabel(sessionType, rotationFormat)}
          </Badge>
          <span className="text-xs font-semibold text-gray-400">Game {number}</span>
          {game.courtNumber != null && (
            <Badge variant="outline" className="border-gray-200 text-[10px] font-semibold text-gray-500">
              Lap {game.courtNumber}
            </Badge>
          )}
        </div>
        {scheduled ? (
          <Badge variant="outline" className="border-0 text-xs font-semibold text-gray-400">
            Belum Main
          </Badge>
        ) : hasSets ? (
          <span className="text-xs font-semibold text-gray-500">
            Set {winsA} - {winsB}
          </span>
        ) : null}
      </div>

      <div className="mt-3 space-y-2">
        {sideRow(labelA, winnerA, scoreLine('a', game))}
        {sideRow(labelB, winnerB, scoreLine('b', game))}
      </div>

      {canEdit && (
        <div className="mt-3 flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onEditScore}
            className="h-10 flex-1 rounded-full border-gray-200 text-sm font-semibold text-[#0E1726]"
          >
            <Icon icon="mdi:pencil-outline" className="h-4 w-4" />
            Edit Skor
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={hasScore}
            onClick={onEditLineup}
            className="h-10 flex-1 rounded-full border-gray-200 text-sm font-semibold text-[#0E1726]"
          >
            <Icon icon="mdi:account-switch-outline" className="h-4 w-4" />
            Edit Player
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={`Hapus Game ${number}`}
            onClick={onDelete}
            className="h-10 w-10 rounded-full border-red-100 text-red-500 hover:bg-red-50 hover:text-red-600"
          >
            <Icon icon="mdi:trash-can-outline" className="h-4 w-4" />
          </Button>
        </div>
      )}
    </Card>
  );
}
